import { buildBackendUrl } from '../config';
const MENU_STORAGE_KEY = 'textile_erp_menus';
const TOKEN_KEY = 'textile_erp_token';
/**
 * Decode base64 encoded data from API/localStorage.
 *
 * atob() decodes base64 into a "binary string" (one JS char per byte) — it does not
 * reassemble multi-byte UTF-8 sequences, so any non-ASCII character (—, •, etc.) comes
 * out mojibake'd. The backend encodes with Buffer.from(json).toString('base64'), which
 * is UTF-8-correct; TextDecoder here undoes that the same way, byte-for-byte.
 */
function decodeMenuData(encoded) {
    try {
        const binaryString = atob(encoded);
        const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
        const jsonString = new TextDecoder('utf-8').decode(bytes);
        return JSON.parse(jsonString);
    }
    catch (error) {
        console.error('Failed to decode menu data:', error);
        return null;
    }
}
/**
 * Get encoded menus from localStorage (stored as received from API)
 */
function getStoredEncodedMenus() {
    try {
        return localStorage.getItem(MENU_STORAGE_KEY);
    }
    catch (error) {
        console.error('Failed to get stored menus:', error);
        return null;
    }
}
/**
 * Store encoded menus in localStorage (as received from API)
 */
function storeEncodedMenus(encodedData) {
    try {
        localStorage.setItem(MENU_STORAGE_KEY, encodedData);
    }
    catch (error) {
        console.error('Failed to store menus:', error);
    }
}
/**
 * Clear stored menus (call on logout)
 */
export function clearStoredMenus() {
    localStorage.removeItem(MENU_STORAGE_KEY);
    // Reset the singleton state
    menuServiceState.encodedData = null;
    menuServiceState.decodedData = null;
    menuServiceState.isLoading = false;
    menuServiceState.fetchPromise = null;
}
// Singleton state to prevent multiple API calls
const menuServiceState = {
    encodedData: null,
    decodedData: null,
    isLoading: false,
    fetchPromise: null,
};
/**
 * Fetch menus from API (internal - only called once)
 * API returns encoded data: { data: "base64encodedstring" }
 */
async function fetchMenusFromApi() {
    const token = localStorage.getItem(TOKEN_KEY);
    const response = await fetch(buildBackendUrl('/api/identity/user/menus'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch menus: ${response.status}`);
    }
    const result = await response.json();
    // API returns { data: "base64encodedstring" }
    if (!result.data) {
        throw new Error('Invalid API response: missing encoded data');
    }
    return result.data;
}
/**
 * Check if user has a valid token
 */
function hasValidToken() {
    const token = localStorage.getItem(TOKEN_KEY);
    return !!token && token.length > 0;
}
/**
 * Get menus - returns cached data or fetches from API once
 * Data is decoded only when this function is called
 * If menus are missing but user has valid token, fetches from API automatically
 */
export async function getMenus(forceRefresh = false) {
    // If force refresh, clear cache
    if (forceRefresh) {
        menuServiceState.encodedData = null;
        menuServiceState.decodedData = null;
        menuServiceState.fetchPromise = null;
        localStorage.removeItem(MENU_STORAGE_KEY);
    }
    // Return cached decoded data if available
    if (menuServiceState.decodedData) {
        return menuServiceState.decodedData;
    }
    // Try to get encoded data from in-memory cache
    let encodedData = menuServiceState.encodedData;
    // If not in memory, try localStorage
    if (!encodedData) {
        encodedData = getStoredEncodedMenus();
        if (encodedData) {
            menuServiceState.encodedData = encodedData;
        }
    }
    // If we have encoded data, decode and return it
    if (encodedData) {
        const decoded = decodeMenuData(encodedData);
        if (decoded && decoded.menus && decoded.menus.length > 0) {
            menuServiceState.decodedData = decoded;
            return decoded;
        }
    }
    // No cached data available - check if user has valid token
    // If user has valid token but no menus, fetch from API
    if (!hasValidToken()) {
        // No token, return empty data
        return { menus: [], paths: [], scopes: [], pathToMenuId: {} };
    }
    // If already fetching, wait for the existing promise
    if (menuServiceState.fetchPromise) {
        return menuServiceState.fetchPromise;
    }
    // User has valid token but no menus - fetch from API
    menuServiceState.isLoading = true;
    menuServiceState.fetchPromise = fetchMenusFromApi()
        .then(encodedData => {
        // Store encoded data in memory and localStorage
        menuServiceState.encodedData = encodedData;
        storeEncodedMenus(encodedData);
        // Decode for use
        const decoded = decodeMenuData(encodedData);
        if (!decoded) {
            throw new Error('Failed to decode menu data from API');
        }
        menuServiceState.decodedData = decoded;
        return decoded;
    })
        .catch(error => {
        console.error('Failed to fetch menus:', error);
        throw error;
    })
        .finally(() => {
        menuServiceState.isLoading = false;
        menuServiceState.fetchPromise = null;
    });
    return menuServiceState.fetchPromise;
}
/**
 * Get accessible paths only (for route protection)
 */
export async function getAccessiblePaths(forceRefresh = false) {
    const data = await getMenus(forceRefresh);
    return data.paths;
}
/**
 * Get user scopes (for permission checking)
 */
export async function getUserScopes(forceRefresh = false) {
    const data = await getMenus(forceRefresh);
    return data.scopes || [];
}
/**
 * Check if user has a specific scope
 */
export async function hasScope(scopeName, forceRefresh = false) {
    const scopes = await getUserScopes(forceRefresh);
    return scopes.includes(scopeName);
}
/**
 * Check if user has any of the specified scopes
 */
export async function hasAnyScope(scopeNames, forceRefresh = false) {
    const scopes = await getUserScopes(forceRefresh);
    return scopeNames.some(name => scopes.includes(name));
}
/**
 * Check if user has all of the specified scopes
 */
export async function hasAllScopes(scopeNames, forceRefresh = false) {
    const scopes = await getUserScopes(forceRefresh);
    return scopeNames.every(name => scopes.includes(name));
}
/**
 * Check if menus are currently loading
 */
export function isMenusLoading() {
    return menuServiceState.isLoading;
}
/**
 * Refresh menus from API (force fetch)
 */
export async function refreshMenus() {
    return getMenus(true);
}
//# sourceMappingURL=menu.service.js.map